/**
 * Auth HTTP service (PX-W6.1) — the ONE module that touches the access token.
 *
 * The token lives in this module's memory ONLY (AC-1 / I-1): never in the
 * store, never in web storage, never on a URL. The refresh token is never
 * read by the client at all — it is an httpOnly `refreshToken` cookie the
 * browser attaches itself (`withCredentials` on the shared client).
 *
 * `subscribeAccessToken` exposes a `useSyncExternalStore`-shaped subscription
 * so the WebSocket provider can re-arm reactively when a silent refresh lands
 * a new token (mirrors xelerate-ai's `authService.http.ts`).
 *
 * Wire contracts (server `AuthController`):
 *  - `POST /auth/login` `{email, password}` → `{accessToken, user}` or
 *    `{twoFactorRequired: true, sessionToken}`
 *  - `POST /auth/register` `{name, email, password}` → `201 <user>` (no token)
 *  - `POST /auth/logout` → `200` (cookie-carried)
 *  - `GET /auth/me` → `<user>` (`MongoUser.forApiResponse`)
 */

import { apiClient, registerTokenStore, type ApiError } from '@/services'

import type { SessionUser } from '../slices/sessionSlice'

/** The access token, in module memory only. */
let accessToken: string | null = null

const listeners = new Set<() => void>()

const emit = (): void => {
  for (const listener of listeners) {
    try {
      listener()
    } catch {
      // A crashing listener must not break the others.
    }
  }
}

/** Read the in-memory access token (null when signed out). */
export function getAccessToken(): string | null {
  return accessToken
}

/**
 * Store a new access token in memory and notify subscribers. Called after
 * login and after every successful silent refresh.
 */
export function setAccessToken(token: string): void {
  accessToken = token
  emit()
}

/** Wipe the in-memory access token and notify subscribers. */
export function clearAccessToken(): void {
  accessToken = null
  emit()
}

/** `useSyncExternalStore`-shaped subscription; returns an unsubscribe fn. */
export function subscribeAccessToken(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange)
  return () => {
    listeners.delete(onStoreChange)
  }
}

// Wire the shared transport's token store at import time. The wrapped setter
// resets the transport's session-expired latch on every fresh token.
registerTokenStore(getAccessToken, setAccessToken, clearAccessToken)

/** What `POST /auth/login` can answer with. */
interface LoginResponse {
  accessToken?: string
  twoFactorRequired?: boolean
  sessionToken?: string
  user?: SessionUser
}

/** The 2FA branch: a valid credential whose account requires a second factor. */
const TWO_FACTOR_REQUIRED: ApiError = {
  status: 401,
  backendKey: 'auth.two-factor.required',
  message: 'This account requires two-factor authentication.'
}

/** A 200 whose body is missing the fields the client needs. */
const malformed = (what: string): ApiError => ({
  status: 0,
  backendKey: 'network',
  message: `login response carried no ${what}`
})

/**
 * Exchange email + password for a session. On success the access token is
 * stored in module memory (the refresh cookie is httpOnly and already set by
 * the browser).
 *
 * @returns The signed-in user (`forApiResponse` shape).
 * @throws ApiError `backendKey: 'auth.two-factor.required'` when the account
 *   needs a second factor (the full 2FA flow is out of scope for W6.1; the
 *   login page surfaces the notice).
 */
export async function login(email: string, password: string): Promise<SessionUser> {
  const payload = await apiClient.post<LoginResponse>('/auth/login', { email, password })
  if (payload.twoFactorRequired === true) {
    throw TWO_FACTOR_REQUIRED
  }
  if (typeof payload.accessToken !== 'string' || payload.accessToken === '') {
    throw malformed('accessToken')
  }
  setAccessToken(payload.accessToken)
  const { user } = payload
  if (!user) {
    throw malformed('user')
  }
  return user
}

/**
 * Whether the server accepts anonymous sign-up (`GET /auth/config`). Any failure
 * answers false: a sign-up link that can only 403 is worse than a missing one.
 */
export async function loadSelfRegistrationEnabled(): Promise<boolean> {
  try {
    const response = await apiClient.get<{ selfRegistrationEnabled?: unknown }>('/auth/config')
    return response.selfRegistrationEnabled === true
  } catch {
    return false
  }
}

/**
 * Register a new account. The backend returns the user only — NO token, no
 * cookie (D8) — so the caller signs the user in immediately afterwards.
 *
 * @returns The freshly created user.
 */
export async function register(name: string, email: string, password: string): Promise<SessionUser> {
  return apiClient.post<SessionUser>('/auth/register', { name, email, password })
}

/**
 * End the session server-side (revokes the refresh cookie) and clear the
 * in-memory token. Transport errors are swallowed on purpose: signing out must
 * always complete locally even when the server call fails.
 */
export async function logout(): Promise<void> {
  try {
    await apiClient.post('/auth/logout')
  } finally {
    clearAccessToken()
  }
}

/**
 * What `GET /auth/me` answers with: `{"user": <MongoUser.forApiResponse>}` —
 * `AuthController.me` wraps the row in a `user` envelope, unlike register
 * which returns the user bare. Unwrapped here so every caller sees ONE shape.
 */
interface MeResponse {
  user?: SessionUser
}

/**
 * Read the current user (`GET /auth/me`). Used as the boot probe by
 * `AuthGuard`: when the httpOnly refresh cookie exists, the shared client
 * silently refreshes and this resolves authenticated.
 *
 * @returns The current user (unwrapped from the `user` envelope).
 * @throws ApiError `network` when the 200 body carries no `user` (malformed).
 */
export async function me(): Promise<SessionUser> {
  const payload = await apiClient.get<MeResponse>('/auth/me')
  const { user } = payload
  if (!user || typeof user.id !== 'string') {
    throw malformed('user')
  }
  return user
}

/**
 * Probe for an existing session from a PUBLIC page without the transport's
 * session-expired bounce (TASK-7F3EE34E fix wave 1, V1). `/accept-invite` sits
 * outside the guards, so on a hard load nothing restores the session, and the
 * guard's own probe (`GET /auth/me`) would answer a signed-out visitor with a
 * 401, a failed silent refresh, and a redirect to
 * `/login?reason=session-expired`. Here the refresh cookie is exchanged
 * directly: `/auth/refresh` is excluded from the silent refresh, so a visitor
 * without a session gets a plain rejection and stays on the page. A token
 * already in memory (in-app navigation) skips the exchange.
 *
 * @returns The signed-in user.
 * @throws ApiError when there is no session (the refresh 401s) or a 200 body is malformed.
 */
export async function restoreSession(): Promise<SessionUser> {
  if (getAccessToken() === null) {
    const refreshed = await apiClient.post<{ accessToken?: unknown }>('/auth/refresh')
    if (typeof refreshed.accessToken !== 'string' || refreshed.accessToken === '') {
      throw malformed('accessToken')
    }
    setAccessToken(refreshed.accessToken)
  }
  return me()
}
